import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_BUSINESS_TIME_ZONE } from 'config/time';
import { RequiredFields } from 'reusable/useRequiredFields';
import { CompanyField, CompanyValues, listTimeZones, OperatorCompany, TimeZoneOption } from './operatorApi';
import { inputClass } from './OperatorUi';

export const emptyCompany = (): CompanyValues => ({
  name: '',
  postal_code: '',
  prefecture: '',
  city: '',
  street: '',
  phone: '',
  time_zone: DEFAULT_BUSINESS_TIME_ZONE,
  memo: '',
});

export const companyValues = (company: OperatorCompany): CompanyValues => ({
  name: company.name ?? '',
  postal_code: company.postal_code ?? '',
  prefecture: company.prefecture ?? '',
  city: company.city ?? '',
  street: company.street ?? '',
  phone: company.phone ?? '',
  time_zone: company.time_zone || DEFAULT_BUSINESS_TIME_ZONE,
  memo: company.memo ?? '',
});

/** 送る前に前後の空白を除く（メモは改行を保つため、そのまま）。 */
export const trimmedCompany = (values: CompanyValues): CompanyValues => {
  const result = { ...values };
  (Object.keys(result) as CompanyField[]).forEach((key) => {
    if (key !== 'memo') result[key] = result[key].trim();
  });
  return result;
};

/** 時間帯として選べる名前（会社の設定の画面と同じ、サーバーの一覧）。読めなければ failed。 */
export function useTimeZones() {
  const [zones, setZones] = useState<TimeZoneOption[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    listTimeZones()
      .then((list) => {
        if (active) setZones(list);
      })
      .catch((error) => {
        console.error('Failed to fetch time zones', error);
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);
  return { zones, failed };
}

const TEXT_FIELDS: { key: Exclude<CompanyField, 'time_zone' | 'memo'>; label: string; autoComplete: string }[] = [
  { key: 'name', label: 'operator.company.field-name', autoComplete: 'organization' },
  { key: 'postal_code', label: 'operator.company.field-postal-code', autoComplete: 'postal-code' },
  { key: 'prefecture', label: 'operator.company.field-prefecture', autoComplete: 'address-level1' },
  { key: 'city', label: 'operator.company.field-city', autoComplete: 'address-level2' },
  { key: 'street', label: 'operator.company.field-street', autoComplete: 'street-address' },
  { key: 'phone', label: 'operator.company.field-phone', autoComplete: 'tel' },
];

interface CompanyFieldsProps {
  values: CompanyValues;
  onChange: (key: CompanyField, value: string) => void;
  /** 会社名の欄の id（必須の確かめでフォーカスを移す）。 */
  nameId: string;
  requiredFields: RequiredFields<'name'>;
  zones: TimeZoneOption[];
  zonesFailed: boolean;
  disabled?: boolean;
}

/** 会社の項目（会社名は必須。ほかは任意）。登録と編集で同じものを使う。 */
const CompanyFields: React.FC<CompanyFieldsProps> = ({
  values,
  onChange,
  nameId,
  requiredFields: required,
  zones,
  zonesFailed,
  disabled = false,
}) => {
  const { t } = useTranslation('layout');
  const baseId = useId();
  const zoneId = `${baseId}-time-zone`;
  const zoneHintId = `${baseId}-time-zone-hint`;
  const memoId = `${baseId}-memo`;
  // 一覧に無い今の値（一覧を読めなかったときなど）も選べるようにする（黙って別の値にしない）。
  const options = zones.some((zone) => zone.name === values.time_zone)
    ? zones
    : [{ name: values.time_zone, utc_offset: '' }, ...zones];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {TEXT_FIELDS.map(({ key, label, autoComplete }) => {
        const id = key === 'name' ? nameId : `${baseId}-${key}`;
        return (
          <div key={key} className={`flex flex-col gap-1 ${key === 'name' || key === 'street' ? 'md:col-span-2' : ''}`}>
            <label htmlFor={id} className="text-subtitle-2 text-neutral/300">
              {t(label)}
              {key === 'name' ? (
                <span className="ml-1 text-caption text-red-700" aria-hidden="true">
                  {t('operator.required-mark')}
                </span>
              ) : null}
            </label>
            <input
              id={id}
              type={key === 'phone' ? 'tel' : 'text'}
              autoComplete={autoComplete}
              className={inputClass}
              value={values[key]}
              disabled={disabled}
              onChange={(event) => {
                onChange(key, event.target.value);
                if (key === 'name') required.clear('name');
              }}
              {...(key === 'name' ? required.fieldProps('name') : {})}
            />
            {key === 'name' ? required.message('name') : null}
          </div>
        );
      })}
      <div className="flex flex-col gap-1 md:col-span-2">
        <label htmlFor={zoneId} className="text-subtitle-2 text-neutral/300">
          {t('operator.company.field-time-zone')}
        </label>
        <select
          id={zoneId}
          className={inputClass}
          value={values.time_zone}
          disabled={disabled}
          aria-describedby={zoneHintId}
          onChange={(event) => onChange('time_zone', event.target.value)}
        >
          {options.map((zone) => (
            <option key={zone.name} value={zone.name}>
              {zone.utc_offset ? `${zone.name} (UTC${zone.utc_offset})` : zone.name}
            </option>
          ))}
        </select>
        <p id={zoneHintId} className="text-caption text-neutral/100">
          {zonesFailed ? t('operator.company.time-zones-failed') : t('operator.company.time-zone-hint')}
        </p>
      </div>
      <div className="flex flex-col gap-1 md:col-span-2">
        <label htmlFor={memoId} className="text-subtitle-2 text-neutral/300">
          {t('operator.company.field-memo')}
        </label>
        <textarea
          id={memoId}
          rows={3}
          className="w-full rounded-md border border-neutral/100 px-3 py-2 text-body-2 text-neutral/300 focus:border-primary/300 focus:outline-none"
          value={values.memo}
          disabled={disabled}
          onChange={(event) => onChange('memo', event.target.value)}
        />
      </div>
    </div>
  );
};

export default CompanyFields;
